import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, UserPlus, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";

/**
 * Reusable "pick a Praticante" dialog.
 * Configurable via props: icon color, title, description, target route builder.
 */
export default function SelectPraticanteDialog({
  open,
  onOpenChange,
  icon: Icon,
  iconBg = "bg-[#EDE1F3]",
  iconColor = "text-[#7B569A]",
  title,
  description,
  ctaLabel = "Continuar",
  ctaClass = "bg-brand-lilac hover:bg-[#9670B0] text-white",
  onSelect,                    // (praticante) => void — called on confirm
  novoPraticanteQuery = "",    // query string when clicking "Cadastrar novo" (e.g., "tipo=em_avaliacao")
  testIdPrefix = "select-praticante",
}) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [praticantes, setPraticantes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setQ(""); setSelectedId(null);
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/praticantes");
        if (!cancelled) setPraticantes(data);
      } catch (e) {
        if (!cancelled) toast.error("Erro ao carregar praticantes");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let arr = praticantes;
    if (term) arr = arr.filter((p) => (p.nome || "").toLowerCase().includes(term));
    return arr.slice().sort((a, b) => {
      const rank = (t) => (t === "em_avaliacao" ? 0 : t === "ativo" ? 1 : t === "lista_espera" ? 2 : 3);
      const r = rank(a.tipo) - rank(b.tipo);
      if (r !== 0) return r;
      return (a.nome || "").localeCompare(b.nome || "");
    });
  }, [praticantes, q]);

  const handleContinue = (target) => {
    const pid = target || selectedId;
    if (!pid) { toast.error("Selecione um praticante"); return; }
    const p = praticantes.find((x) => x.id === pid);
    onOpenChange(false);
    onSelect(p || { id: pid });
  };

  const handleNovoPraticante = () => {
    onOpenChange(false);
    const q = novoPraticanteQuery ? `&${novoPraticanteQuery}` : "";
    navigate(`/praticantes?novo=1${q}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          {Icon && (
            <div className={`mx-auto h-12 w-12 rounded-full ${iconBg} flex items-center justify-center mb-2`}>
              <Icon className={`h-6 w-6 ${iconColor}`} />
            </div>
          )}
          <DialogTitle className="text-center font-heading">{title}</DialogTitle>
          {description && <DialogDescription className="text-center">{description}</DialogDescription>}
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Digite o nome do praticante…"
              className="pl-9 h-11"
              data-testid={`${testIdPrefix}-search`}
            />
          </div>

          <div className="max-h-72 overflow-y-auto rounded-lg border border-border">
            {loading ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Carregando…</div>
            ) : filtered.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground space-y-3">
                <p>Nenhum praticante encontrado{q ? ` para "${q}"` : ""}.</p>
                <Button variant="outline" size="sm" onClick={handleNovoPraticante} data-testid={`${testIdPrefix}-novo`}>
                  <UserPlus className="h-4 w-4 mr-1" /> Cadastrar novo praticante
                </Button>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {filtered.map((p) => {
                  const isSel = selectedId === p.id;
                  return (
                    <li key={p.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(p.id)}
                        onDoubleClick={() => handleContinue(p.id)}
                        data-testid={`${testIdPrefix}-item-${p.id}`}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${isSel ? "bg-[#F0FBFC] ring-1 ring-inset ring-brand-turquoise" : "hover:bg-slate-50"}`}
                      >
                        {p.foto_url ? (
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${p.foto_url}`}
                            alt=""
                            className="h-9 w-9 rounded-full object-cover border border-border"
                          />
                        ) : (
                          <div className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold text-brand-turquoise">
                            {(p.nome || "?").slice(0, 2).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{p.nome}</p>
                          <p className="text-[11px] text-muted-foreground">
                            {p.data_nasc ? `Nasc.: ${new Date(p.data_nasc).toLocaleDateString("pt-BR")}` : "—"}
                            {p.responsavel_nome_snapshot && ` · Resp.: ${p.responsavel_nome_snapshot}`}
                          </p>
                        </div>
                        {p.tipo === "em_avaliacao" && (
                          <Badge className="bg-[#EDE1F3] text-[#7B569A] border-0">Em avaliação</Badge>
                        )}
                        {p.tipo === "ativo" && (
                          <Badge className="bg-emerald-100 text-emerald-700 border-0">Ativo</Badge>
                        )}
                        {p.tipo === "lista_espera" && (
                          <Badge className="bg-amber-100 text-amber-700 border-0">Lista de espera</Badge>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {filtered.length > 0 && (
            <div className="text-center">
              <button
                type="button"
                onClick={handleNovoPraticante}
                className="text-xs text-brand-turquoise hover:underline inline-flex items-center gap-1"
              >
                <UserPlus className="h-3 w-3" /> Não encontrou? Cadastrar novo praticante
              </button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={() => handleContinue()}
            disabled={!selectedId}
            className={ctaClass}
            data-testid={`${testIdPrefix}-continuar`}
          >
            {ctaLabel} <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
